import React, {Fragment, useState, useEffect, useContext} from 'react';
import {Table, Tag, Typography, Button, Popconfirm, Row, Col, Select, notification, Space, message, Tooltip} from 'antd';
import {useRouteMatch} from 'react-router-dom';
import {OpenApiContext} from '../openapi-context';
import {AppContext} from '../state';
import {map, get, filter, merge, clone, cloneDeep, pickBy, identity, isUndefined, omit, has} from 'lodash';
import {snakeCase} from 'change-case';
import {DeleteOutlined, LinkOutlined} from '@ant-design/icons';
import {FieldNameAndDescriptionEditor} from './field-name-description-editor';
import {EditableCell} from './editable-cell';
import {MasterSchemaView} from './master-schema-view';
import {SchemaPropType} from './schema-prop-type';
import {AddSchemaFieldBulkDialog} from './add-schema-field-bulk-dialog';
import {AddSchemaRefFieldDialog} from './add-schema-ref-field-dialog';
import {MarkdownView} from './markdown-block';
import {OpenAPIRef} from './openapi-ref';

async function buildTableModel(properties = [], required = [], openapi, context) {
    return Promise.all(
        map(properties, async (spec, name) => {
            const state = [];
            let tempSpec = cloneDeep(spec);

            if (spec.$ref) {
                const refSpec = spec;
                // clone so sibling overrides don't mutate the shared lookup
                // result (otherwise later consumers see stale overrides).
                tempSpec = cloneDeep(await openapi.state.lookup(spec.$ref, context)) || {};
                tempSpec.$ref = refSpec.$ref;
                // 3.1 Reference Object allows `description` and `summary`
                // siblings next to `$ref` that override the referenced node.
                // 3.0 already allowed this for Schema Objects via JSON Schema.
                tempSpec.description = refSpec.description || tempSpec.description;
                if (refSpec.summary) tempSpec.summary = refSpec.summary;

                if (refSpec.type === 'array') {
                    tempSpec.items = {
                        type: refSpec.type,
                        properties: tempSpec.properties,
                    };
                    tempSpec.type = refSpec.type;
                }
            }

            if (Array.isArray(tempSpec.allOf)) {
                const resolvedSpecs = await openapi.state.lookupMany(tempSpec.allOf, context);
                resolvedSpecs.map((resolved, idx) => {
                    if (resolved.properties) {
                        tempSpec.type = 'object';
                        tempSpec.subType = 'allOf';
                        tempSpec.subTypeDetails = tempSpec.allOf[idx].$ref || tempSpec.allOf[idx];
                        merge(tempSpec, resolved);
                    } else if (resolved.items) {
                        tempSpec.type = 'array';
                        tempSpec.subType = 'allOf';
                        tempSpec.subTypeDetails = tempSpec.allOf[idx].$ref || tempSpec.allOf[idx];
                        merge(tempSpec, resolved);
                    }
                });
            }
            required.indexOf(name) !== -1 && state.push({text: 'required', color: 'green'});
            tempSpec.readOnly && state.push({text: 'read-only', color: 'gold'});
            tempSpec.writeOnly && state.push({text: 'write-only', color: 'magenta'});
            tempSpec.deprecated && state.push({text: 'deprecated', color: 'red'});
            tempSpec.nullable && state.push({text: 'nullable', color: 'cyan'});

            return pickBy(
                {
                    key: snakeCase(name),
                    title: tempSpec.title,
                    type: tempSpec.type,
                    subType: tempSpec.subType,
                    subTypeDetails: tempSpec.subTypeDetails,
                    example: tempSpec.example,
                    format: tempSpec.format,
                    items: tempSpec.items,
                    itemType: get(tempSpec, 'items.type'),
                    enum: tempSpec.enum || [],
                    properties: tempSpec.properties,
                    name,
                    description: tempSpec.description || '',
                    summary: tempSpec.summary,
                    state,
                    required: tempSpec.required || [],
                    externalDocs: tempSpec.externalDocs,
                    minLength: tempSpec.minLength,
                    maxLength: tempSpec.maxLength,
                    minimum: tempSpec.minimum,
                    maximum: tempSpec.maximum,
                    $ref: tempSpec.$ref,
                },
                identity
            );
        })
    );
}

// OpenAPI allows `example` to be any JSON value (string, number, bool, array,
// object). React throws if asked to render a non-primitive as a child, so we
// normalize here: primitives render as-is, complex values stringify as JSON.
// Mirrors the safe-render pattern in parameter-table.js.
function formatExampleValue(value) {
    if (value == null) return '';
    const t = typeof value;
    if (t === 'string' || t === 'number' || t === 'boolean') return String(value);
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

// Scope IDs to the top-level SchemaView only. Nested MasterSchemaView calls
// supply contextPath so their sub-properties could be addressed later, but
// for Phase 1 we only link to top-level fields (most common review use-case).
//
// URL form: just the field name (or dotted contextPath.fieldName for nested).
// No prefix — only one type of anchor on this page today. Will add a prefix
// if we start linking to webhooks/ops/params on the same view and collisions
// become possible.
function fieldAnchorId(contextPath, fieldName) {
    return contextPath ? `${contextPath}.${fieldName}` : fieldName;
}

// Convert the schema-view's contextPath (lodash-style dot path inside the
// current root object — `properties.foo`, `items.properties.bar`, etc.) +
// the URL params into the absolute JSON pointer the §9.4 contract expects:
//
//   /components/schemas/Address/properties/line1
//   /components/parameters/Pagination/schema/properties/limit
//   /paths/~1location/get/responses/200/content/application~1json/schema/properties/foo
//
// For the top-level views we ship today (models / parameters /
// securitySchemes), this is straightforward. Interfaces / webhooks will
// gain similar anchors when we wire onCommentAnchorChange to clicks
// inside the operation block (Phase A+).
function buildJsonPointer({view, element, schemaType, contextPath, fieldName}) {
    const VIEW_PREFIX = {
        models: '/components/schemas',
        parameters: '/components/parameters',
        securitySchemes: '/components/securitySchemes',
    };
    const prefix = VIEW_PREFIX[view];
    if (!prefix || !element) return null;

    // Field path inside the schema object — same logic as the existing
    // makeChildContextPath. When contextPath is supplied (a nested
    // SchemaView) we sit inside a sub-schema; otherwise we're at the
    // root and prepend properties / items.properties.
    const innerPath = contextPath
        ? `${contextPath}.${fieldName}`
        : (schemaType === 'array' ? `items.properties.${fieldName}` : `properties.${fieldName}`);

    // RFC 6901: '/' and '~' need escaping. Field names rarely contain
    // them, but contextPath segments built from snakeCase'd refs might.
    const segments = innerPath.split('.').map((s) => s.replace(/~/g, '~0').replace(/\//g, '~1'));
    return `${prefix}/${element}/${segments.join('/')}`;
}

export function SchemaView({schema, type, isArray, resolvedRef, editMode, onChange, triggerEdit, contextPath, offset = 0}) {
    const openapiContext = useContext(OpenApiContext);
    const {editorProps = {}} = useContext(AppContext);
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version/:view/:element');
    const [fields, setFields] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [columns, setColumns] = useState([]);
    const [editing, setEditing] = useState();
    // Rename-ahead buffer as *state* (not ref) so the match logic below
    // can fall through to it when a rename is mid-flight. Sequence:
    //
    //   onBlur (rename) -> setPendingRename('zip')  -> re-render
    //   ... buildTableModel runs ...
    //   setFields(newFields) + setEditing('zip') + setPendingRename(null)
    //
    // In between the setPendingRename and the setFields commit, fields
    // still carries the old row; the row.key === editing match holds on
    // the OLD name. Once setFields lands, the row.key === pendingRename
    // match catches it on the NEW name. Either way, the row never
    // unmatches and the pink edit form never drops to read-mode.
    const [pendingRename, setPendingRename] = useState(null);
    const matchesEditing = (record) => {
        if (!record) return false;
        const keys = [record.key, record.name];
        return keys.includes(editing) || (pendingRename && keys.includes(pendingRename));
    };

    // Deep-link resolve: when the URL has #field-xxx, scroll that row into
    // view once the fields have rendered. Re-run on field changes so a
    // navigation that lands on a different model still resolves the hash
    // inside the new content.
    useEffect(() => {
        if (!fields || fields.length === 0) return;
        const hash = (typeof window !== 'undefined' && window.location.hash) || '';
        if (!hash || hash.length < 2) return;
        // Double rAF: give antd Table one frame to paint rows, then scroll.
        const id = hash.slice(1);
        const raf1 = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({behavior: 'smooth', block: 'center'});
                    el.classList.add('field-anchor-flash');
                    window.setTimeout(() => el.classList.remove('field-anchor-flash'), 1500);
                }
            });
        });
        return () => window.cancelAnimationFrame(raf1);
    }, [fields]);

    const copyFieldLink = (anchorId) => () => {
        const url = `${window.location.origin}${window.location.pathname}#${anchorId}`;
        const write = navigator.clipboard && navigator.clipboard.writeText
            ? navigator.clipboard.writeText(url)
            : Promise.reject(new Error('clipboard unavailable'));
        write.then(
            () => message.success('Link copied'),
            () => message.error('Unable to copy; please copy manually: ' + url),
        );
    };

    // §9.4: notify portal when the user selects a property so the
    // Comments tab can refresh against (spec_path, json_pointer). No-op
    // when the editor isn't portal-mounted (standalone test/dev).
    const fireAnchorChange = (record) => {
        const cb = editorProps.onCommentAnchorChange;
        if (typeof cb !== 'function') return;
        const json_pointer = buildJsonPointer({
            view: params.view,
            element: params.element,
            schemaType: type,
            contextPath,
            fieldName: record.name,
        });
        if (!json_pointer) return; // unsupported view (e.g. nested in interface), skip
        cb({
            spec_path: params.category && params.key ? `${params.category}/${params.key}` : null,
            json_pointer,
        });
    };

    if (isUndefined(type)) {
        if (has(schema, 'items.properties')) {
            type = 'array';
        } else {
            type = 'object';
        }
    }

    function isExpandable(record) {
        if (record.type === 'object') {
            return true;
        } else if (record.type === 'array') {
            if (editMode) {
                if (!isUndefined(record.items) && record.items.$ref) {
                    return false;
                } else if (!isUndefined(record.items) && record.items) {
                    return record.items.type === 'object' || record.items.type === 'array';
                } else {
                    return record.type === 'object' || record.type === 'array';
                }
            } else if (!isUndefined(record.items)) {
                return record.items.type === 'object' || record.items.type === 'array' || record.items.$ref;
            } else {
                return record.type === 'object' || record.type === 'array';
            }
        }
    }

    function makeChildContextPath(contextPath, record) {
        if (contextPath) {
            return `${contextPath}.${record.name}`;
        } else {
            if (type === 'array') {
                return `items.properties.${record.name}`;
            } else {
                return `properties.${record.name}`;
            }
        }
    }

    const expandableConfig = () => {
        if (filter(fields, (f) => f.type === 'array' || f.type === 'object').length > 0) {
            return {
                expandedRowRender: (record) => {
                    if (record.type === 'array') {
                        return (
                            <MasterSchemaView
                                schema={clone(record)}
                                type="array"
                                offset={offset + 1}
                                editMode={editMode}
                                contextPath={makeChildContextPath(contextPath, record)}
                                onChange={onChange}
                                triggerEdit={triggerEdit}
                            />
                        );
                    } else {
                        return (
                            <MasterSchemaView
                                schema={clone(record)}
                                offset={offset + 1}
                                editMode={editMode}
                                contextPath={makeChildContextPath(contextPath, record)}
                                onChange={onChange}
                                triggerEdit={triggerEdit}
                            />
                        );
                    }
                },
                rowExpandable: isExpandable,
                indentSize: 15,
            };
        } else {
            return false;
        }
    };

    const isSchemaRef = () => resolvedRef;

    const rowSelectionConfig = () => {
        if (isSchemaRef()) {
            return null;
        }
        // Pending rename highlights the NEW row key even before the fields
        // array has rebuilt with that row — avoids the radio-selection
        // flashing off during the rename transition.
        const selected = [];
        if (editing) selected.push(editing);
        if (pendingRename && pendingRename !== editing) selected.push(pendingRename);
        return editMode || (!editMode && triggerEdit)
            ? {
                  type: 'radio',
                  selectedRowKeys: selected,
                  onSelect(record) {
                      if (!editMode && triggerEdit) {
                          triggerEdit();
                      }
                      setEditing(record.key);
                  },
              }
            : null;
    };

    // Always register EditableCell as the body cell. When editMode flips
    // false, the `columns` state still carries onCell props from the
    // previous edit-mode render cycle, and antd's default Cell2 would
    // spread them straight onto <td>, leaking `editor`/`editMode` to the
    // DOM (React warns). EditableCell strips those regardless of mode.
    const components = () => ({body: {cell: EditableCell}});
    const removeField = (record) => () => {
        const changeSet = {key: record.name, op: 'remove'};
        if (type === 'array') {
            changeSet.propName = contextPath ? `${contextPath}.items.properties.${record.name}` : `items.properties.${record.name}`;
        } else {
            changeSet.propName = contextPath ? `${contextPath}.properties.${record.name}` : `properties.${record.name}`;
        }
        onChange(changeSet);
    };

    const addField = (spec) => {
        if (!Array.isArray(spec)) {
            spec = [spec];
        }

        return onChange({
            op: 'add',
            fields: spec.map((s) => {
                s.key = snakeCase(s.name);
                if (type === 'array') {
                    s.propName = contextPath ? `${contextPath}.items.properties.${s.name}` : `items.properties.${s.name}`;
                } else {
                    s.propName = contextPath ? `${contextPath}.properties.${s.name}` : `properties.${s.name}`;
                }
                return s;
            }),
        });
    };

    const addFieldRef = (spec) => {
        if (!spec.name) {
            const changeSet = {
                op: 'set-schema-ref',
                link: spec.$ref,
                type: spec.type,
                schemaPath: contextPath,
            };
            onChange(changeSet);
        } else {
            spec.op = 'add';
            spec.key = snakeCase(spec.name);
            if (type === 'array') {
                spec.propName = contextPath ? `${contextPath}.items.properties.${spec.name}` : `items.properties.${spec.name}`;
            } else {
                spec.propName = contextPath ? `${contextPath}.properties.${spec.name}` : `properties.${spec.name}`;
            }
            onChange({op: 'add', fields: [omit(spec, 'op')]});
        }
    };

    const removeSchemaRef = () => {
        const changeSet = {key: '$ref', op: 'remove'};
        if (isArray) {
            changeSet.propName = contextPath ? `${contextPath}.items.$ref` : `items.$ref`;
        } else {
            changeSet.propName = contextPath ? `${contextPath}.$ref` : `$ref`;
        }
        // console.log('execute remove $ref change set', changeSet);
        onChange(changeSet);
    };

    const changeSchemaType = (newType) => {
        const changeSet = {op: 'change-schema-type', targetType: newType};
        if (contextPath) {
            changeSet.schemaPath = contextPath;
        }
        onChange(changeSet);
    };

    const makeFieldContextPath = (schemaType, contextPath) => {
        if (contextPath) {
            return schemaType === 'array' ? `${contextPath}.items.properties` : `${contextPath}.properties`;
        } else {
            return schemaType === 'array' ? `items.properties` : `properties`;
        }
    };

    useEffect(() => {
        async function createSchemaModel() {
            if (schema) {
                try {
                    setLoading(true);
                    const newFields = type === 'array'
                        ? await buildTableModel(get(schema, 'items.properties') || {}, get(schema, 'items.required'), openapiContext, params)
                        : await buildTableModel(get(schema, 'properties'), get(schema, 'required'), openapiContext, params);
                    // Commit setFields and any pending rename setEditing
                    // in the same React tick. React 18 auto-batches state
                    // updates inside async callbacks, so both land in one
                    // render — otherwise the row key and the editing key
                    // disagree for one render cycle and the pink edit
                    // form briefly drops to read-mode (the flicker).
                    setFields(newFields);
                    if (pendingRename) {
                        if (newFields.some((f) => f.key === pendingRename || f.name === pendingRename)) {
                            setEditing(pendingRename);
                            setPendingRename(null);
                        }
                    }
                } catch (err) {
                    console.error(err.message);
                    notification.error({title: err.message, placement: 'bottomRight'});
                } finally {
                    setLoading(false);
                }
            }
        }
        createSchemaModel();
    }, [schema]);

    useEffect(() => {
        let cols = [
            {
                title: 'Name',
                key: 'name',
                editor: (props) => <FieldNameAndDescriptionEditor {...props} contextPath={makeFieldContextPath(type, contextPath)} />,
                dataIndex: 'name',
                render: (name, record) => {
                    const anchorId = fieldAnchorId(contextPath, record.name);
                    return (
                    <div id={anchorId} className="propertyCell" onClick={() => fireAnchorChange(record)}>
                        <p className="propertyName">
                            {name}{' '}
                            <span className="propertyType">
                                : <SchemaPropType record={record} />
                            </span>
                            <Tooltip title="Copy link to this field">
                                <Button
                                    className="propertyCopyLink"
                                    type="text"
                                    size="small"
                                    icon={<LinkOutlined />}
                                    onClick={copyFieldLink(anchorId)}
                                />
                            </Tooltip>
                        </p>
                        {record.summary && (
                            <p className="propertySummary">
                                <Typography.Text strong>{record.summary}</Typography.Text>
                            </p>
                        )}
                        <MarkdownView className="propertyDescription" source={record.description} />
                        {record.enum.length > 0 && (
                            <p>
                                enum of{' '}
                                {record.enum.map((v, idx) => (
                                    <Tag style={{marginTop: '5px'}} color="geekblue" size="small" key={idx}>
                                        {v}
                                    </Tag>
                                ))}
                            </p>
                        )}
                        {record.example !== undefined && record.example !== null && (
                            <p className="propertyDescription">
                                ex: <Typography.Text code>{formatExampleValue(record.example)}</Typography.Text>
                            </p>
                        )}
                        {record.externalDocs && (
                            <div>
                                <a href={record.externalDocs.url}>
                                    <p>{record.externalDocs.url}</p>
                                </a>
                                <p className="propertyDescription">{record.externalDocs.description ? <Typography.Text code>{formatExampleValue(record.externalDocs.description)}</Typography.Text> : null}</p>
                            </div>
                        )}
                        {record.minLength && (
                            <p className="propertyDescription">
                                minLength: <Typography.Text code>{record.minLength}</Typography.Text>
                            </p>
                        )}
                        {record.maxLength && (
                            <p className="propertyDescription">
                                maxLength: <Typography.Text code>{record.maxLength}</Typography.Text>
                            </p>
                        )}
                        {record.minimum && (
                            <p className="propertyDescription">
                                min: <Typography.Text code>{record.minimum}</Typography.Text>
                            </p>
                        )}
                        {record.maximum && (
                            <p className="propertyDescription">
                                max: <Typography.Text code>{record.maximum}</Typography.Text>
                            </p>
                        )}
                    </div>
                );
                },
            },
            {
                // Flags used to be edited via FieldStateEditor (5 vertical
                // checkboxes) in this cell, which forced a 20% column
                // width and compressed the Description MD editor next
                // door. Flags now live as a horizontal row inside the
                // Name editor form, so this column is display-only and
                // much narrower — tags still update live as the user
                // flips checkboxes next door.
                title: 'State',
                dataIndex: 'state',
                key: 'state',
                width: '12%',
                render: (state) =>
                    state.map((entry) => (
                        <Tag key={entry.text} color={entry.color}>
                            {entry.text}
                        </Tag>
                    )),
            },
        ];

        if (editMode) {
            cols.push({
                width: '10%',
                render: (_, record) =>
                    !isSchemaRef() && (
                        <Popconfirm okType="default" title={`Remove field ${record.key} from schema?`} onConfirm={removeField(record)}>
                            <Button className="deleteIcon" size="small" type="danger" icon={<DeleteOutlined />} />
                        </Popconfirm>
                    ),
            });

            cols = cols.map((col) =>
                Object.assign(col, {
                    onCell: (record) => {
                        const rowKey = record.key || record.name;
                        const isEditingThisRow =
                            editing === rowKey ||
                            record.name === editing ||
                            (pendingRename && (pendingRename === rowKey || pendingRename === record.name));
                        // When a row is being edited the Name cell takes
                        // over the State column too (colSpan 2 / 0), so
                        // the expanded form reclaims the ~12% of width
                        // that was otherwise a dead gutter next to the
                        // MD editor. Read-mode rows keep the 3-column
                        // layout with their State tags.
                        let colSpan;
                        if (isEditingThisRow) {
                            if (col.key === 'name') colSpan = 2;
                            else if (col.key === 'state') colSpan = 0;
                        }
                        return {
                            record,
                            editMode,
                            // Pass a flag the cell can use directly so it
                            // doesn't have to re-derive the match.
                            isEditingThisRow,
                            editing,
                            editor: col.editor,
                            dataIndex: col.dataIndex,
                            title: col.title,
                            ...(colSpan !== undefined ? {colSpan} : {}),
                            handleSave: (changeSet) => {
                                if (changeSet.name) {
                                    // Queue the rename target as state so
                                    // the match logic can fall through to
                                    // it during the 2-3 renders it takes
                                    // for the new fields array to arrive.
                                    setPendingRename(changeSet.name);
                                }
                                return onChange(changeSet);
                            },
                        };
                    },
                })
            );
        }
        setColumns(cols);
    }, [schema, editMode, editing, pendingRename]);

    return (
        <Row>
            <Col xs={24 - offset} offset={offset}>
                <Table
                    size="small"
                    components={components()}
                    expandable={expandableConfig()}
                    isLoading={isLoading}
                    rowSelection={rowSelectionConfig()}
                    rowClassName={(record) => (matchesEditing(record) ? 'edit-selected-row' : '')}
                    showHeader={false}
                    dataSource={fields}
                    columns={columns}
                    className="columnStyle"
                    pagination={
                        fields && fields.length > 25
                            ? {
                                  defaultPageSize: 25,
                              }
                            : false
                    }
                />
                {!isSchemaRef() ? (
                    editMode && (
                        <div className="schemaFooter" id="schema-view">
                            <Space>
                                {schema && (
                                    <span>
                                        Schema Type:{' '}
                                        <Select style={{width: '16rem'}} placeholder="select schema type" value={type} onChange={changeSchemaType}>
                                            <Select.Option value="object">object</Select.Option>
                                            <Select.Option value="array">array</Select.Option>
                                            <Select.Option value="allOf">combined: allOf</Select.Option>
                                            <Select.Option value="oneOf">combined: oneOf</Select.Option>
                                            <Select.Option value="anyOf">combined: anyOf</Select.Option>
                                        </Select>
                                    </span>
                                )}
                                {schema && <AddSchemaFieldBulkDialog onAddField={addField} />}
                                {schema && <AddSchemaRefFieldDialog onAddField={addFieldRef} />}
                            </Space>
                        </div>
                    )
                ) : (
                    <div className="schemaFooter">
                        <p className="schemaReferenceBar">
                            {isArray ? (
                                <span>
                                    Array of <OpenAPIRef link={resolvedRef} />
                                </span>
                            ) : (
                                <span>
                                    Reference to <OpenAPIRef link={resolvedRef} />
                                </span>
                            )}
                            {editMode && (
                                <Fragment>
                                    <Tag color="yellow">read-only</Tag>
                                    <Popconfirm title="Do you want to remove the referenced schema?" onConfirm={removeSchemaRef}>
                                        <Button className="deleteRef" danger icon={<DeleteOutlined />}>
                                            Delete Ref
                                        </Button>
                                    </Popconfirm>
                                </Fragment>
                            )}
                        </p>
                    </div>
                )}
            </Col>
        </Row>
    );
}
